import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Modal, ModalContent, ModalDescription, ModalTitle } from '@/components/ui/Modal';
import { toast } from '@/hooks/useToast';
import { nieuweId } from '@/lib/melden/id';
import { api, formatApiError } from '@/lib/api';
import { naarBijlage } from '@/lib/melden/beeld';
import { bewaarConcept, leesConcept, wisConcept, type MeldConcept } from '@/lib/melden/concept';
import { bouwContext } from '@/lib/melden/context';
import { elementOnder, pinSelector } from '@/lib/melden/pin';
import { platSlaan, type Vorm } from '@/lib/melden/tekenen';
import type { MeldBijlage, MeldPin } from '@/lib/melden/types';
import { BeeldBewerker } from './BeeldBewerker';
import { MeldFormulier } from './MeldFormulier';
import { MeldingVerstuurd } from './MeldingVerstuurd';

export interface Opname { gemaskeerd: HTMLCanvasElement | null; leesbaar: HTMLCanvasElement | null }

/** P5: the report panel. One external_id per panel: a double click is one melding. */
export function MeldPaneel({ opname, onSluit }: { opname: Opname; onSluit: () => void }) {
  const { t, i18n } = useTranslation();
  const [externalId] = useState(() => nieuweId());
  const [concept, setConcept] = useState<MeldConcept>(leesConcept);
  const [beeldAan, setBeeldAan] = useState(true);
  const [toon, setToon] = useState(false);
  const [vormen, setVormen] = useState<Vorm[]>([]);
  const [pin, setPin] = useState<MeldPin | null>(null);
  const [verstuurd, setVerstuurd] = useState<string | null>(null);
  const basis = toon ? opname.leesbaar : opname.gemaskeerd;

  function wijzig(c: MeldConcept) {
    setConcept(c);
    bewaarConcept(c);
  }

  function plaatsPin(p: { x: number; y: number } | null) {
    const root = document.getElementById('root');
    const el = p && root ? elementOnder(p.x * window.innerWidth, p.y * window.innerHeight, root) : null;
    const selector = el && root ? pinSelector(el, root) : null;
    setPin(p && selector ? { selector, x: p.x, y: p.y } : null);
  }

  const verstuur = useMutation({
    mutationFn: async () => {
      const plat = beeldAan && basis ? platSlaan(basis, vormen) : null;
      const beeld = plat ? await naarBijlage(plat, !toon) : null;
      const bijlagen: MeldBijlage[] = beeld ? [beeld] : [];
      return api.meldingen.maak({
        external_id: externalId,
        titel: concept.titel.trim(),
        omschrijving: concept.omschrijving.trim(),
        soort: concept.soort,
        ernst: concept.ernst,
        context: bouwContext(window, i18n.language, import.meta.env.VITE_GIT_SHA as string | undefined),
        // The pin only goes with a picture that is actually sent.
        pin: beeld ? pin : null,
        wil_mens: false,
        bijlagen,
      });
    },
    onSuccess: (r) => {
      wisConcept();
      setVerstuurd(r.external_id);
    },
    onError: (e) => toast.error(t('melden.fout'), { description: formatApiError(e, t('melden.fout')) }),
  });

  const klaar = concept.titel.trim().length > 0 && concept.omschrijving.trim().length > 0;

  return (
    <Modal open onOpenChange={(o) => { if (!o) onSluit(); }}>
      <ModalContent data-capture="exclude" className="w-[min(calc(100vw-32px),720px)]">
        <ModalTitle>{t('melden.titel')}</ModalTitle>
        {verstuurd ? (
          <MeldingVerstuurd externalId={verstuurd} />
        ) : (
          <>
            <ModalDescription className="mt-1 text-[13px] text-text-secondary">{t('melden.uitleg')}</ModalDescription>
            <MeldFormulier concept={concept} onChange={wijzig} />
            <BeeldBewerker
              basis={basis}
              aan={beeldAan}
              onAan={setBeeldAan}
              toon={toon}
              onToon={setToon}
              kanTonen={opname.leesbaar !== null}
              vormen={vormen}
              onVormen={setVormen}
              onPin={plaatsPin}
            />
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                disabled={!klaar || verstuur.isPending}
                onClick={() => verstuur.mutate()}
                className="h-8 rounded-sm bg-accent px-3 text-[13px] font-medium text-on-accent hover:bg-accent/90 disabled:opacity-60"
              >
                {t('melden.versturen')}
              </button>
            </div>
          </>
        )}
        <Link to="/meldingen" onClick={onSluit} className="mt-4 inline-block text-[12.5px] text-text-secondary underline">
          {t('melden.mijnLink')}
        </Link>
      </ModalContent>
    </Modal>
  );
}
